
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  HomeIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";

export default function Sidebar({ mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = mode === "dark";

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const permissions = user.permissions || {};

  const hasFullAccess = ["SuperAdmin", "Admin"].includes(user.role);

  const hasPermission = (module, action = "list") => {
    if (hasFullAccess) return true;
    const modulePerms = permissions[module];
    if (!modulePerms) return false;
    return Array.isArray(modulePerms) && modulePerms.includes(action);
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  const [openDropdown, setOpenDropdown] = useState(null);

  // ← Add ref for the dropdown container
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    // Only add listener when a dropdown is open
    if (openDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openDropdown]); // Re-run when openDropdown changes

  const menuItems = [
    { text: "Dashboard", icon: HomeIcon, path: "/" },
    {
      text: "User",
      icon: UsersIcon,
      dropdown: true,
      id: "user",
      subItems: [
        { text: "All Users", path: "/staff", show: hasPermission("staff_management", "list") },
        { text: "Roles", path: "/roles", show: hasPermission("role_management", "list") },
      ],
    },
    { text: "Tasks", icon: ClipboardDocumentListIcon, path: "/tasks", module: "tasks" },
    { text: "Teams", icon: UserGroupIcon, path: "/teams", module: "team_management" },
    {
      text: "Projects",
      icon: ClipboardDocumentListIcon,
      path: "/projects",
      module: "project_management",
    },
    { text: "Leads", icon: UsersIcon, path: "/leads", module: "leads" },
    // { text: "Prospects", icon: ArrowTrendingUpIcon, path: "/prospects", module: "prospects" },
    // { text: "Quotations", icon: DocumentTextIcon, path: "/quotations", module: "quotations" },
    { text: "Settings", icon: Cog6ToothIcon, path: "/settings", module: "settings" },
  ];

  const baseBtn = `px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition cursor-pointer`;
  const activeBtn = isDark ? "bg-white text-slate-900" : "bg-slate-900 text-white";
  const idleBtn = isDark ? "text-slate-300 hover:bg-slate-800" : "text-slate-700 hover:bg-gray-200";

  return (
    <nav className="flex items-center gap-2 bg-white px-3 py-2 rounded-full shadow-md border border-gray-200 dark:bg-slate-800 dark:border-slate-700">
      {menuItems.map((item) => {
        if (item.module && !hasPermission(item.module)) return null;

        if (item.dropdown) {
          const visibleSubItems = item.subItems.filter((sub) => sub.show !== false);
          if (visibleSubItems.length === 0) return null;

          const isDropdownActive = visibleSubItems.some((sub) => isActive(sub.path));

          return (
            // ← Attach the ref here (wraps both button + dropdown)
            <div key={item.id} className="relative" ref={dropdownRef}>
              <button
                onClick={() => setOpenDropdown(openDropdown === item.id ? null : item.id)}
                className={`${baseBtn} ${isDropdownActive ? activeBtn : idleBtn}`}
              >
                <item.icon className="w-4 h-4" />
                {item.text}
                <ChevronDownIcon
                  className={`w-4 h-4 transition-transform ${openDropdown === item.id ? "rotate-180" : ""}`}
                />
              </button>

              {openDropdown === item.id && (
                <div
                  className={`absolute top-full left-0 mt-2 w-48 rounded-xl border shadow-lg z-50
                    ${isDark ? "bg-slate-800 border-slate-700" : "bg-white border-gray-200"}`}
                >
                  {visibleSubItems.map((sub) => (
                    <button
                      key={sub.path}
                      onClick={() => {
                        navigate(sub.path);
                        setOpenDropdown(null);
                      }}
                      className={`w-full text-left px-4 py-3 text-sm font-medium
                        ${isDark ? "hover:bg-slate-700" : "hover:bg-gray-100"}
                        ${isActive(sub.path) ? (isDark ? "bg-slate-700" : "bg-gray-100") : ""}`}
                    >
                      {sub.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        }

        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`${baseBtn} ${isActive(item.path) ? activeBtn : idleBtn}`}
          >
            <item.icon className="w-4 h-4" />
            {item.text}
          </button>
        );
      })}
    </nav>
  );
}